import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, NavLink, Outlet, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { reactionTypes } from '#app/components/ui/reactions.js'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { publicProfileReactionFilter } from '#app/utils/reactions.server.js'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			Reaction: {
				select: { id: true, type: true, commentId: true, scriptId: true, guideId: true, newsPostId: true, fanSiteId: true },
				// Tab counts must match what the tabs can actually list.
				where: publicProfileReactionFilter,
			},
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	return json({ owner })
}

export default function UserReactionsRoute() {
	const { owner } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Reactions ({owner.Reaction.length})</h1>
				</div>
			</div>

			{/* Reaction type tabs */}
			<div className="mt-6 flex flex-wrap gap-2">
				{reactionTypes.map((reaction) => {
					const count = owner.Reaction.filter((r) => r.type === reaction.type).length
					return (
						<NavLink
							key={reaction.type}
							to={reaction.type}
							className={({ isActive }) =>
								`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
									isActive
										? 'border-primary bg-primary/10 text-primary'
										: 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
								}`
							}
						>
							<span>{reaction.icon}</span>
							<span>{reaction.label}</span>
							<span className="rounded-full bg-muted-foreground/10 px-1.5 py-0.5 text-xs">{count}</span>
						</NavLink>
					)
				})}
			</div>

			{/* Content area */}
			<div className="mt-6">
				<Outlet />
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
